import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import T from '../theme';

function useIsMobile(bp = 768) {
  const [m, setM] = useState(window.innerWidth < bp);
  useEffect(() => { const h = () => setM(window.innerWidth < bp); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, [bp]);
  return m;
}

const getMenuItems = (isAdmin) => {
  const items = [{ path: '/', icon: '🏠', label: 'Bosh sahifa' }];
  if (isAdmin) {
    items.push({ path: '/admin/devices', icon: '📡', label: 'Qurilmalar' });
    items.push({ path: '/admin/users', icon: '👥', label: 'Foydalanuvchilar' });
  } else {
    items.push({ path: '/children', icon: '👶', label: 'Bolalar' });
    items.push({ path: '/zones', icon: '📍', label: 'Hududlar' });
    items.push({ path: '/relatives', icon: '👨‍👩‍👧', label: 'Qarindoshlar' });
    items.push({ path: '/logs', icon: '📋', label: 'Qaydlar' });
  }
  items.push({ path: '/settings', icon: '⚙️', label: 'Sozlamalar' });
  return items;
};

export default function Layout() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Mobilda sahifa o'zgarganda menuni yopish
  useEffect(() => {
    if (isMobile) setMobileMenuOpen(false);
  }, [location.pathname, isMobile]);

  // Desktop sidebar toggle
  useEffect(() => {
    if (!isMobile) setMobileMenuOpen(false);
  }, [isMobile]);

  const menuItems = getMenuItems(isAdmin);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const linkStyle = (isActive) => ({
    display: 'flex', alignItems: 'center', gap: 12,
    padding: isMobile ? '14px 18px' : '12px 16px',
    borderRadius: 12, fontSize: isMobile ? 15 : 14, fontWeight: 700,
    color: isActive ? T.blue : T.textSec,
    background: isActive ? T.blueSoft : 'transparent',
    textDecoration: 'none', transition: 'all 0.2s',
    border: isActive ? `1px solid ${T.blueBorder}` : '1px solid transparent',
  });

  // ===== MOBILE LAYOUT =====
  if (isMobile) {
    return (
      <div style={{ minHeight: '100vh', fontFamily: T.font, background: T.bg }}>
        {/* Top bar */}
        <header style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200,
          background: '#fff', borderBottom: `1px solid ${T.border}`,
          padding: '0 16px', height: 56,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} style={{
              width: 40, height: 40, borderRadius: 10, background: T.bg,
              border: `1px solid ${T.border}`, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 20,
            }}>{mobileMenuOpen ? '✕' : '☰'}</button>
            <div style={{
              width: 32, height: 32, borderRadius: 10,
              background: `linear-gradient(135deg, ${T.blue}, ${T.blueLight})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16,
            }}>🛡</div>
            <span style={{ fontSize: 16, fontWeight: 900, color: T.text }}>SafeChild</span>
          </div>
          <div style={{
            width: 34, height: 34, borderRadius: 10,
            background: `linear-gradient(135deg, ${T.blue}, ${T.blueLight})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, color: '#fff', fontWeight: 800,
          }}>
            {user?.first_name?.[0]?.toUpperCase() || '?'}
          </div>
        </header>

        {/* Overlay */}
        {mobileMenuOpen && (
          <div onClick={() => setMobileMenuOpen(false)} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            zIndex: 300, backdropFilter: 'blur(2px)',
          }} />
        )}

        {/* Slide menu */}
        <nav style={{
          position: 'fixed', top: 0, left: 0, bottom: 0,
          width: 280, background: '#fff', zIndex: 400,
          transform: mobileMenuOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.25s ease',
          display: 'flex', flexDirection: 'column',
          boxShadow: mobileMenuOpen ? '4px 0 24px rgba(0,0,0,0.1)' : 'none',
        }}>
          {/* Logo */}
          <div style={{
            padding: '20px 18px', borderBottom: `1px solid ${T.border}`,
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12,
              background: `linear-gradient(135deg, ${T.blue}, ${T.blueLight})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 20, boxShadow: `0 4px 12px ${T.blue}40`,
            }}>🛡</div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 900, color: T.text }}>SafeChild</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: 1 }}>
                {isAdmin ? 'ADMIN' : 'OTA-ONA'}
              </div>
            </div>
          </div>

          {/* Menu items */}
          <div style={{ flex: 1, padding: '12px', overflowY: 'auto' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {menuItems.map((item) => (
                <NavLink key={item.path} to={item.path} end={item.path === '/'} style={({ isActive }) => linkStyle(isActive)}>
                  <span style={{ fontSize: 20, width: 28, textAlign: 'center' }}>{item.icon}</span>
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          </div>

          {/* User + logout */}
          <div style={{ padding: '12px', borderTop: `1px solid ${T.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: T.bg, borderRadius: 12, marginBottom: 8 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                background: `linear-gradient(135deg, ${T.blue}, ${T.blueLight})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 16, color: '#fff', fontWeight: 800,
              }}>{user?.first_name?.[0]?.toUpperCase() || '?'}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{user?.first_name || user?.username}</div>
                <div style={{ fontSize: 11, color: T.textMuted }}>{isAdmin ? 'Admin' : 'Ota-ona'}</div>
              </div>
            </div>
            <button onClick={handleLogout} style={{
              width: '100%', padding: '12px', background: T.coralSoft,
              border: '1px solid #FCA5A5', borderRadius: 10,
              fontSize: 14, fontWeight: 700, color: '#DC2626',
              cursor: 'pointer', fontFamily: T.font,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}>🚪 Chiqish</button>
          </div>
        </nav>

        {/* Main content */}
        <main style={{ paddingTop: 56, minHeight: '100vh' }}>
          <div style={{ padding: '16px' }}>
            <Outlet />
          </div>
        </main>
      </div>
    );
  }

  // ===== DESKTOP LAYOUT =====
  return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: T.font, background: T.bg }}>
      <aside style={{
        width: sidebarOpen ? 260 : 72, background: '#fff',
        borderRight: `1px solid ${T.border}`, display: 'flex',
        flexDirection: 'column', transition: 'width 0.3s',
        position: 'fixed', left: 0, top: 0, bottom: 0, zIndex: 100,
      }}>
        <div style={{ padding: '20px 16px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 12,
            background: `linear-gradient(135deg, ${T.blue}, ${T.blueLight})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 20, flexShrink: 0, boxShadow: `0 4px 12px ${T.blue}40`,
          }}>🛡</div>
          {sidebarOpen && (
            <div>
              <div style={{ fontSize: 18, fontWeight: 900, color: T.text }}>SafeChild</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: 1 }}>{isAdmin ? 'ADMIN' : 'OTA-ONA'}</div>
            </div>
          )}
        </div>
        <nav style={{ flex: 1, padding: '16px 12px', overflowY: 'auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {menuItems.map((item) => (
              <NavLink key={item.path} to={item.path} end={item.path === '/'} style={({ isActive }) => linkStyle(isActive)}>
                <span style={{ fontSize: 18, width: 24, textAlign: 'center' }}>{item.icon}</span>
                {sidebarOpen && <span>{item.label}</span>}
              </NavLink>
            ))}
          </div>
        </nav>
        <div style={{ padding: '16px 12px', borderTop: `1px solid ${T.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: T.bg, borderRadius: 12, marginBottom: 8 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: `linear-gradient(135deg, ${T.blue}, ${T.blueLight})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16, color: '#fff', fontWeight: 800, flexShrink: 0,
            }}>{user?.first_name?.[0]?.toUpperCase() || user?.username?.[0]?.toUpperCase() || '?'}</div>
            {sidebarOpen && (
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.first_name || user?.username}</div>
                <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 600 }}>{isAdmin ? 'Admin' : 'Ota-ona'}</div>
              </div>
            )}
          </div>
          <button onClick={handleLogout} style={{
            width: '100%', padding: '10px 12px', background: T.coralSoft,
            border: '1px solid #FCA5A5', borderRadius: 10, fontSize: 13, fontWeight: 700,
            color: '#DC2626', cursor: 'pointer', fontFamily: T.font,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}>🚪{sidebarOpen && <span>Chiqish</span>}</button>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} style={{
          position: 'absolute', right: -14, top: 28, width: 28, height: 28,
          borderRadius: '50%', background: '#fff', border: `1px solid ${T.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', fontSize: 12, color: T.textMuted, boxShadow: T.shadow,
        }}>{sidebarOpen ? '◀' : '▶'}</button>
      </aside>
      <main style={{ flex: 1, marginLeft: sidebarOpen ? 260 : 72, transition: 'margin-left 0.3s', minHeight: '100vh' }}>
        <div style={{ padding: '24px 32px', maxWidth: 1400 }}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
